import { AxiosError } from "axios";
import { handlerErrorFunctions } from "../../../../utils/errorHlandlerUtils";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import SpendingDemandAPI from "../../services/SpendingDemandAPI";
import { useEffect } from "react";
import userAPI from "../../../user/services/user.api";
import { SpendingDemand } from "../../types/spendingDemand.types";
import { Spending } from "../../../spending/types/spending.types";
import { FileUrl } from "../../../../components/molecules/FileListManager/FileListManager";

export const useSingleSpendingDemandCore = (
  singleSpendingDemandId: string,
  handleNetworkErrorFunction:
    | ((error: AxiosError<unknown, any>, SreenContext: string | null) => void)
    | null,
  handleInternErrorFunction:
    | ((errorMessage: string | null, errorCode: number | null) => void)
    | null,
  employeeId: string,
  disableQuery: boolean
) => {
  const { handleNetworkError } = handlerErrorFunctions(
    handleNetworkErrorFunction,
    handleInternErrorFunction
  );

  const queryClient = useQueryClient();


  const historicStatus = useQuery({
    queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
    queryFn: () =>
      SpendingDemandAPI.getFetchHistoricStatusSpendingDemand(
        singleSpendingDemandId
      ),
  });

  const querySingleSpendingDemand = useQuery({
    queryFn: () =>
      SpendingDemandAPI.getFetchSingleSpendingDemandAPIProtectedV2(
        singleSpendingDemandId,
        employeeId,
        
      ),
    queryKey: ["spendingdemand", singleSpendingDemandId],
    enabled: !disableQuery
  });

  const sendAskForUpdateSpendingDemand = useMutation<
    any,
    any,
    { message: string; spendingDemandIdSpecific?: string }
  >({
    mutationFn: ({
      message,
      spendingDemandIdSpecific,
    }: {
      message: string;
      spendingDemandIdSpecific?: string;
    }) => {
      return SpendingDemandAPI.askForUpdateSpendingDemandAPIProtected(
        spendingDemandIdSpecific || singleSpendingDemandId,
        message
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const sendRefuseSpendingDemand = useMutation<
    any,
    any,
    { message: string; spendingDemandIdSpecific?: string }
  >({
    mutationFn: ({
      message,
      spendingDemandIdSpecific,
    }: {
      message: string;
      spendingDemandIdSpecific?: string;
    }) => {
      return SpendingDemandAPI.refuseSpendingDemandAPIProtected(
        spendingDemandIdSpecific || singleSpendingDemandId,
        message
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const sendValidateSpendingDemand = useMutation<
    any,
    any,
    { message: string; spendingDemandIdSpecific?: string }
  >({
    mutationFn: ({
      spendingDemandIdSpecific,
      message,
    }: {
      spendingDemandIdSpecific?: string;
      message?: string;
    }) => {
      return SpendingDemandAPI.validateSpendingDemandAPIProtected(
        spendingDemandIdSpecific || singleSpendingDemandId,
        message
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const sendCancelSpendingDemand = useMutation<
    any,
    any,
    { message: string; spendingDemandIdSpecific?: string }
  >({
    mutationFn: ({
      spendingDemandIdSpecific,
      message,
    }: {
      spendingDemandIdSpecific?: string;
      message?: string;
    }) => {
      return SpendingDemandAPI.cancelSpendingDemandAPIProtected(
        spendingDemandIdSpecific || singleSpendingDemandId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const managerWaitingQueries = useQueries({
    queries: (
      querySingleSpendingDemand?.data
        ?.manager_employee_id_waiting_for_validation ?? []
    ).map((managerIdWaiting) => {
      return {
        queryKey: ["users", managerIdWaiting],
        queryFn: async () => {
          return userAPI.getSingleUserProtectedAPIV2(managerIdWaiting);
        },
      };
    }),
  });

  const modifySpendingDemand = useMutation<any, any, {spendingDemandDto: Partial<SpendingDemand>, files?: FileUrl[]}>({
    mutationFn: async ({spendingDemandDto, files}: {spendingDemandDto: Partial<SpendingDemand>, files?: FileUrl[]}) => {
      try {
        const result = await SpendingDemandAPI.modifySpendingDemandAPIProtected(
          singleSpendingDemandId,
          spendingDemandDto
        );
        if(files && files.length > 0){
          // Attendre que tous les fichiers soient uploadés avant de continuer
          await Promise.all(
            files
              .filter((file) => !file.toNotUpload)
              .map((file) => SpendingDemandAPI.addNewFileToSpendingDemandAPI(singleSpendingDemandId, file))
          );
        }
        return result;
      } catch (e) {
        const error = e as AxiosError;
        handleNetworkError(error, "modifySpendingDemand");
        throw e;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemand", singleSpendingDemandId, "files"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemand", singleSpendingDemandId] });
      queryClient.refetchQueries({ queryKey: ["spendingdemand", singleSpendingDemandId, "files"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const setPayedSingleSpending = useMutation<
    any,
    any,
    { callback?: (c: { result?: Spending; err?: any }) => void }
  >({
    mutationFn: async ({ callback }) => {
      if (!singleSpendingDemandId) {
        console.error("Spending demand id not found");
      }
      try {
        const spendingCreadtedFromSpendingDemand =
          await SpendingDemandAPI.setPayed(singleSpendingDemandId);
        if (callback) {
          callback({ result: spendingCreadtedFromSpendingDemand });
        }
        return spendingCreadtedFromSpendingDemand;
      } catch (e) {
        const error = e as AxiosError;
        handleNetworkError(error, "allSpendingDemandListQuery");
        if (callback) {
          callback({ err: error });
        }
        throw e;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["spending"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const setCloseSingleSpending = useMutation<
    any,
    any,
    { callback?: (c: { result?: Spending; err?: any }) => void }
  >({
    mutationFn: async ({ callback }) => {
      if (!singleSpendingDemandId) {
        console.error("Spending demand id not found");
      }
      try {
        const spendingDemandClosed =
          await SpendingDemandAPI.setClose(singleSpendingDemandId);
        if (callback) {
          callback({ result: spendingDemandClosed });
        }
        return spendingDemandClosed;
      } catch (e) {
        const error = e as AxiosError;
        handleNetworkError(error, "allSpendingDemandListQuery");
        if (callback) {
          callback({ err: error });
        }
        throw e;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["spending"] });
      queryClient.invalidateQueries({
        queryKey: ["spendingdemand", singleSpendingDemandId, "historic_status"],
      });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
    },
  });

  const getListFilesOfSpendingDemand = useQuery({
    queryKey: ["spendingdemand", singleSpendingDemandId, "files"],
    queryFn: () =>
      SpendingDemandAPI.getListFilesOfSpendingDemandAPI(singleSpendingDemandId),
    enabled: !disableQuery
  });

  const deleteFileOfSpendingDemand = useMutation<any, any, {spendingDemandFileId: string, password?: string}>({
    mutationFn: async ({spendingDemandFileId, password}: {spendingDemandFileId: string, password?: string}) => {
      return SpendingDemandAPI.deleteFileOfSpendingDemandAPI(singleSpendingDemandId, spendingDemandFileId, password);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemandmanager"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["spendingdemand", singleSpendingDemandId, "files"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemand"] });
      queryClient.refetchQueries({ queryKey: ["spendingdemandmanager"] });
      getListFilesOfSpendingDemand.refetch();
    },
  });

  const addFileOfSpendingDemand = useMutation<any, any, {fileURL: FileUrl}>({
    mutationFn: async ({fileURL}: {fileURL: FileUrl}) => {
      return SpendingDemandAPI.addNewFileToSpendingDemandAPI(singleSpendingDemandId, fileURL);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spendingdemand", singleSpendingDemandId, "files"] });
      getListFilesOfSpendingDemand.refetch();
    },
  });

  useEffect(() => {
    if (querySingleSpendingDemand.isError) {
      handleNetworkError(
        querySingleSpendingDemand.error as AxiosError,
        "singleSpendingDemand"
      );
    }

    if (sendAskForUpdateSpendingDemand.isError) {
      handleNetworkError(
        sendAskForUpdateSpendingDemand.error as AxiosError,
        "sendAskForUpdateSpendingDemand"
      );
    }

    if (sendRefuseSpendingDemand.isError) {
      handleNetworkError(
        sendRefuseSpendingDemand.error as AxiosError,
        "sendRefuseSpendingDemand"
      );
    }

    if (sendValidateSpendingDemand.isError) {
      handleNetworkError(
        sendValidateSpendingDemand.error as AxiosError,
        "sendValidateSpendingDemand"
      );
    }

    // Note: setCloseSingleSpending errors are handled locally in components
  }, [ //eslint-disable-line
    querySingleSpendingDemand.isError,
    sendAskForUpdateSpendingDemand.isError, 
    sendRefuseSpendingDemand.isError, 
  ]);

  return {
    singleSpendingDemandData: querySingleSpendingDemand.data,
    isLoading: querySingleSpendingDemand.isLoading,

    sendAskForUpdateSpendingDemand: {
      run: sendAskForUpdateSpendingDemand.mutate,
      isPending: sendAskForUpdateSpendingDemand.isPending,
      isSucess: sendAskForUpdateSpendingDemand.isSuccess,
      isError: sendAskForUpdateSpendingDemand.isError,
    },

    sendRefuseSpendingDemand: {
      run: sendRefuseSpendingDemand.mutate,
      isPending: sendRefuseSpendingDemand.isPending,
      isSucess: sendRefuseSpendingDemand.isSuccess,
      isError: sendRefuseSpendingDemand.isError,
    },
    sendValidateSpendingDemand: {
      run: sendValidateSpendingDemand.mutate,
      isPending: sendValidateSpendingDemand.isPending,
      isSucess: sendValidateSpendingDemand.isSuccess,
      isError: sendValidateSpendingDemand.isError,
    },
    sendCancelSpendingDemand: {
      run: sendCancelSpendingDemand.mutate,
      isPending: sendCancelSpendingDemand.isPending,
      isSucess: sendCancelSpendingDemand.isSuccess,
      isError: sendCancelSpendingDemand.isError,
    },
    managerWaitingValidation: {
      isPending: managerWaitingQueries.some((query) => query.isLoading),
      data: managerWaitingQueries.map((query) => query.data),
      isError: managerWaitingQueries.some((query) => query.isError),
    },
    historicStatus: {
      isPending: historicStatus.isLoading,
      data: historicStatus.data,
      isError: historicStatus.isError,
    },
    modifySpendingDemand: {
      run: modifySpendingDemand.mutate,
      isPending: modifySpendingDemand.isPending,
      isSucess: modifySpendingDemand.isSuccess,
      isError: modifySpendingDemand.isError,
    },
    setPayedSingleSpending: {
      run: setPayedSingleSpending.mutate,
      isPending: setPayedSingleSpending.isPending,
      isSucess: setPayedSingleSpending.isSuccess,
      isError: setPayedSingleSpending.isError,
    },
    setCloseSingleSpending: {
      run: setCloseSingleSpending.mutate,
      isPending: setCloseSingleSpending.isPending,
      isSucess: setCloseSingleSpending.isSuccess,
      isError: setCloseSingleSpending.isError,
    },

    getListFilesOfSpendingDemand: {
      isPending: getListFilesOfSpendingDemand.isLoading,
      data: getListFilesOfSpendingDemand.data,
      isError: getListFilesOfSpendingDemand.isError,
    },
    deleteFileOfSpendingDemand: {
      run: deleteFileOfSpendingDemand.mutate,
      isPending: deleteFileOfSpendingDemand.isPending,
      isSucess: deleteFileOfSpendingDemand.isSuccess,
      isError: deleteFileOfSpendingDemand.isError,
    },
    addFileOfSpendingDemand: {
      run: addFileOfSpendingDemand.mutate,
      isPending: addFileOfSpendingDemand.isPending,
      isSucess: addFileOfSpendingDemand.isSuccess,
      isError: addFileOfSpendingDemand.isError,
    },
    isToProcess : querySingleSpendingDemand.data?.is_to_process || false,
    
  };
};
